import { useState, useEffect, useMemo, useRef } from "react";
import { Drawer } from "../index";
import CustomButton from '../../common/CustomButton';
import UnsavedChangesIndicator from '../../common/UnsavedChangesIndicator';
import SingleSelect from '../../common/Search/SingleSelect';
import CustomTooltip from '../../common/CustomTooltip';
import { HiOutlineInformationCircle, HiOutlineEye, HiOutlineEyeOff, HiOutlineRefresh, HiOutlineSearch, HiCheck, HiOutlineExclamationCircle, HiOutlineFolder, HiOutlineLightningBolt, HiOutlineQuestionMarkCircle } from 'react-icons/hi';

interface ProcessedProject {
    name: string;
    hidden: boolean;
}

interface ManageProjectsModalProps {
    isOpen: boolean;
    onClose: () => void;
    processedProjects: Record<string, { hidden: boolean }>;
    onSave: (projects: Array<{ name: string; hidden: boolean }>) => Promise<void>;
    onResync?: (projectName: string) => Promise<void>;
    integrationType: 'azure' | 'jira';
    isLoading?: boolean;
}

// Enhanced Skeleton for loading state
const ProjectItemSkeleton = () => (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 animate-pulse">
        <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            <div className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            </div>
        </div>
        <div className="w-12 h-7 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
    </div>
);

const WarningContent = () => (
    <div className="space-y-1.5 text-xs">
        <p className="font-semibold mb-1">About Hiding Projects</p>
        <ul className="list-disc pl-4 space-y-0.5">
            <li>Prevents future data imports from hidden projects</li>
            <li>Deactivates associated project teams</li>
            <li>Removes project's sprints and states mapping from the UI</li>
        </ul>
    </div>
);

const ProjectWarningContent = () => (
    <div className="space-y-1.5 text-xs">
        <p className="font-semibold mb-1">Impact of Hiding This Project:</p>
        <ul className="list-disc pl-4 space-y-0.5">
            <li>Stops future data imports</li>
            <li>Deactivates the associated project team</li>
            <li>Removes project's sprints and states mapping from the UI</li>
        </ul>
    </div>
);

// Genie-style animation keyframes - GPU optimized (only transform + opacity)
const genieKeyframes = `
@keyframes genieMinimize {
    0% {
        transform: perspective(1000px) translateX(0) translateY(0) scale(1) rotateX(0deg);
        opacity: 1;
    }
    100% {
        transform: perspective(1000px) translateX(200px) translateY(-80px) scale(0.02) rotateX(12deg);
        opacity: 0;
    }
}

@keyframes collapseHeight {
    0% {
        max-height: 160px;
        margin-bottom: 16px;
    }
    100% {
        max-height: 0;
        margin-bottom: 0;
    }
}
`;

// Welcome/Onboarding Card Component with exit animation support
const WelcomeCard = ({ isVisible, onClose, onExitComplete }: {
    isVisible: boolean;
    onClose: () => void;
    onExitComplete?: () => void;
}) => {
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [shouldRender, setShouldRender] = useState(isVisible);

    useEffect(() => {
        if (isVisible) {
            setShouldRender(true);
            setIsAnimatingOut(false);
        } else if (shouldRender) {
            // Start exit animation
            setIsAnimatingOut(true);
            // Remove from DOM after animation completes
            const timer = setTimeout(() => {
                setShouldRender(false);
                onExitComplete?.();
            }, 350); // Slightly longer than animation for smooth cleanup
            return () => clearTimeout(timer);
        }
    }, [isVisible, shouldRender, onExitComplete]);

    if (!shouldRender) return null;

    const containerStyle: React.CSSProperties = isAnimatingOut ? {
        animation: 'genieMinimize 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards, collapseHeight 0.3s ease-out forwards',
        transformOrigin: 'top left',
        pointerEvents: 'none',
        willChange: 'transform, opacity',
    } : {
        maxHeight: '160px',
        opacity: 1,
        marginBottom: '16px',
        transform: 'perspective(1000px) scale(1)',
        willChange: 'auto',
    };

    return (
        <>
            <style>{genieKeyframes}</style>
            <div style={containerStyle} className="overflow-visible">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 relative">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex-shrink-0">
                            <HiOutlineInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 text-blue-900 dark:text-blue-100">
                            <WarningContent />
                        </div>
                        <button
                            onClick={onClose}
                            disabled={isAnimatingOut}
                            className="absolute top-2 right-2 p-1 text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300 transition-colors disabled:opacity-50"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// Enhanced Stats Bar with clickable filters
const StatsBar = ({
    stats,
    activeFilter,
    onFilterChange
}: {
    stats: { total: number; visible: number; hidden: number };
    activeFilter: 'all' | 'visible' | 'hidden';
    onFilterChange: (filter: 'all' | 'visible' | 'hidden') => void;
}) => (
    <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
        <button
            onClick={() => onFilterChange('all')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${activeFilter === 'all'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
        >
            <HiOutlineFolder className="w-4 h-4" />
            <span>All</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${activeFilter === 'all'
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                {stats.total}
            </span>
        </button>
        <button
            onClick={() => onFilterChange('visible')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${activeFilter === 'visible'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
        >
            <HiOutlineEye className="w-4 h-4 text-green-500" />
            <span>Active</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${activeFilter === 'visible'
                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                {stats.visible}
            </span>
        </button>
        <button
            onClick={() => onFilterChange('hidden')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${activeFilter === 'hidden'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
        >
            <HiOutlineEyeOff className="w-4 h-4 text-gray-400" />
            <span>Hidden</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${activeFilter === 'hidden'
                ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                {stats.hidden}
            </span>
        </button>
    </div>
);

// Project Card Component
const ProjectCard = ({
    project,
    isChanged,
    onToggle,
    onResync,
    syncingProject,
    integrationLabel
}: {
    project: ProcessedProject;
    isChanged: boolean;
    onToggle: () => void;
    onResync?: () => void;
    syncingProject: string | null;
    integrationLabel: string;
}) => {
    const isSyncing = syncingProject === project.name;

    return (
        <div
            className={`group relative flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${project.hidden
                ? 'bg-gray-50/80 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700/50'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md'
                } ${isChanged ? 'ring-2 ring-amber-400/60 ring-offset-2 dark:ring-offset-gray-900' : ''}`}
        >
            {/* Changed indicator badge */}
            {isChanged && (
                <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-semibold rounded-full shadow-sm">
                    Modified
                </div>
            )}

            <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Project Icon with status indicator */}
                <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${project.hidden
                    ? 'bg-gray-100 dark:bg-gray-700/50'
                    : 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30'
                    }`}>
                    <HiOutlineFolder className={`w-5 h-5 transition-colors ${project.hidden ? 'text-gray-400' : 'text-blue-600 dark:text-blue-400'
                        }`} />
                    {/* Status dot */}
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${project.hidden ? 'bg-gray-400' : 'bg-green-500'
                        }`} />
                </div>

                {/* Project Info */}
                <div className="min-w-0 flex-1">
                    <h4 className={`font-medium truncate text-sm ${project.hidden
                        ? 'text-gray-500 dark:text-gray-400'
                        : 'text-gray-900 dark:text-white'
                        }`}>
                        {project.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-flex items-center gap-1 text-xs ${project.hidden
                            ? 'text-gray-400 dark:text-gray-500'
                            : 'text-green-600 dark:text-green-400'
                            }`}>
                            {project.hidden ? (
                                <>
                                    <HiOutlineEyeOff className="w-3 h-3" />
                                    <span>Hidden from workspace</span>
                                </>
                            ) : (
                                <>
                                    <HiOutlineEye className="w-3 h-3" />
                                    <span>Active in workspace</span>
                                </>
                            )}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                {/* Resync Button - Only show for visible projects */}
                {!project.hidden && onResync && (
                    <CustomTooltip
                        title={`Resync all epics in this project`}
                        arrow
                        placement="top"
                    >
                        <button
                            onClick={onResync}
                            disabled={syncingProject !== null}
                            className={`px-2.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-medium ${isSyncing
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <HiOutlineRefresh className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                            <span>{isSyncing ? 'Syncing...' : 'Sync'}</span>
                        </button>
                    </CustomTooltip>
                )}

                {/* Warning Icon for Hidden Projects */}
                {project.hidden && (
                    <CustomTooltip title={<ProjectWarningContent />} arrow placement="left">
                        <div className="text-amber-500 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg cursor-help">
                            <HiOutlineExclamationCircle className="w-5 h-5" />
                        </div>
                    </CustomTooltip>
                )}

                {/* Toggle Button with improved visual */}
                <CustomTooltip
                    title={project.hidden ? 'Click to make active' : 'Click to hide project'}
                    arrow
                    placement="top"
                >
                    <button
                        onClick={onToggle}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 dark:focus:ring-offset-800 ${!project.hidden
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 shadow-inner'
                            : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                    >
                        <span
                            className={`inline-flex items-center justify-center h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-300 ${!project.hidden ? 'translate-x-6' : 'translate-x-1'
                                }`}
                        >
                            {!project.hidden ? (
                                <HiCheck className="w-3 h-3 text-green-600" />
                            ) : (
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                            )}
                        </span>
                    </button>
                </CustomTooltip>
            </div>
        </div>
    );
};

// Empty State Component
const EmptyState = ({
    searchQuery,
    viewFilter,
    onClearFilters
}: {
    searchQuery: string;
    viewFilter: 'all' | 'visible' | 'hidden';
    onClearFilters: () => void;
}) => (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
            {searchQuery ? (
                <HiOutlineSearch className="w-7 h-7 text-gray-400" />
            ) : (
                <HiOutlineFolder className="w-7 h-7 text-gray-400" />
            )}
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
            {searchQuery ? "No matches found" : viewFilter === 'hidden' ? "No hidden projects" : viewFilter === 'visible' ? "No active projects" : "No projects yet"}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-4">
            {searchQuery
                ? "Try a different search term or clear your filters"
                : viewFilter !== 'all'
                    ? `You don't have any ${viewFilter} projects at the moment`
                    : "Projects will appear here once they're imported from your integration"
            }
        </p>
        {(searchQuery || viewFilter !== 'all') && (
            <button
                onClick={onClearFilters}
                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
                Clear all filters
            </button>
        )}
    </div>
);

const StatsBarSkeleton = () => (
    <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse mb-4">
        <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
    </div>
);

const SearchBarSkeleton = () => (
    <div className="h-11 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl animate-pulse mb-4" />
);

const ManageProjectsModal = ({
    isOpen,
    onClose,
    processedProjects,
    onSave,
    onResync,
    integrationType,
    isLoading = false
}: ManageProjectsModalProps) => {
    const [localProjects, setLocalProjects] = useState<ProcessedProject[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [viewFilter, setViewFilter] = useState<'all' | 'visible' | 'hidden'>('all');
    const [syncingProject, setSyncingProject] = useState<string | null>(null);
    const [showWelcome, setShowWelcome] = useState(true);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showResyncConfirmation, setShowResyncConfirmation] = useState(false);
    const [pendingResyncProject, setPendingResyncProject] = useState<string | null>(null);

    const [simpleOriginalProjects, setSimpleOriginalProjects] = useState<Record<string, boolean>>({});

    // Initialize local state from processedProjects
    useEffect(() => {
        if (processedProjects && Object.keys(processedProjects).length > 0) {
            const projectsArray = Object.entries(processedProjects).map(([name, config]) => ({
                name,
                hidden: config.hidden
            }));
            // Sort alphabetically by name
            projectsArray.sort((a, b) => a.name.localeCompare(b.name));
            setLocalProjects(projectsArray);

            // Store original state for diff calculation and stable filtering
            const originalMap: Record<string, boolean> = {};
            projectsArray.forEach(p => {
                originalMap[p.name] = p.hidden;
            });
            setSimpleOriginalProjects(originalMap);
        }
    }, [processedProjects]);

    // Filter projects based on search and view filter
    const filteredProjects = useMemo(() => {
        let filtered = localProjects;

        // Apply view filter using ORIGINAL state to prevent jumping
        if (viewFilter !== 'all') {
            filtered = filtered.filter(project => {
                const originalHidden = simpleOriginalProjects[project.name];
                // Use original state if available (stable during editing), fallback to current
                const effectiveHidden = originalHidden !== undefined ? originalHidden : project.hidden;

                return viewFilter === 'visible' ? !effectiveHidden : effectiveHidden;
            });
        }

        // Apply search (Case insensitive)
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(project =>
                project.name.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [localProjects, searchQuery, viewFilter, simpleOriginalProjects]);

    // Stats calculation
    const stats = useMemo(() => {
        const total = localProjects.length;

        // Calculate stats based on ORIGINAL state to prevent counts from jumping while editing
        const counts = localProjects.reduce((acc, project) => {
            const originalHidden = simpleOriginalProjects[project.name];
            const isHidden = originalHidden !== undefined ? originalHidden : project.hidden;

            if (isHidden) {
                acc.hidden++;
            } else {
                acc.visible++;
            }
            return acc;
        }, { visible: 0, hidden: 0 });

        return { total, ...counts };
    }, [localProjects, simpleOriginalProjects]);

    // Calculate changed projects (diff from original)
    const changedProjects = useMemo(() => {
        return localProjects.filter(project => {
            const originalHidden = simpleOriginalProjects[project.name];
            return originalHidden !== undefined && originalHidden !== project.hidden;
        });
    }, [localProjects, simpleOriginalProjects]);

    const hasChanges = changedProjects.length > 0;

    const handleToggle = (projectName: string) => {
        setLocalProjects(prev =>
            prev.map(project =>
                project.name === projectName
                    ? { ...project, hidden: !project.hidden }
                    : project
            )
        );
        setSaveSuccess(false);
    };

    const handleSave = async () => {
        if (changedProjects.length === 0) return;

        try {
            setIsSaving(true);
            setSaveSuccess(false);
            // Only send the changed projects in the payload
            await onSave(changedProjects);

            // Update the original reference after successful save
            const newOriginalMap: Record<string, boolean> = { ...simpleOriginalProjects };
            changedProjects.forEach(p => {
                newOriginalMap[p.name] = p.hidden;
            });
            setSimpleOriginalProjects(newOriginalMap);

            setSaveSuccess(true);
            // Auto-hide success message after 3 seconds
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving project visibility:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleResync = (projectName: string) => {
        if (!onResync || syncingProject) return;
        setPendingResyncProject(projectName);
        setShowResyncConfirmation(true);
    };

    const confirmResync = async () => {
        if (!onResync || !pendingResyncProject) return;

        try {
            setShowResyncConfirmation(false);
            setSyncingProject(pendingResyncProject);
            await onResync(pendingResyncProject);
        } catch (error) {
            console.error('Error syncing project:', error);
        } finally {
            setSyncingProject(null);
            setPendingResyncProject(null);
        }
    };

    const cancelResync = () => {
        setShowResyncConfirmation(false);
        setPendingResyncProject(null);
    };

    const handleClearFilters = () => {
        setSearchQuery("");
        setViewFilter('all');
    };

    const integrationLabel = integrationType === 'azure' ? 'Azure DevOps' : 'Jira';

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            position="right"
        >
            <div className="h-full bg-gray-50 dark:bg-gray-900 flex flex-col">
                {/* Header */}
                <div className="sticky top-0 z-20 shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 group"
                                title="Back"
                            >
                                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Manage Projects
                                    </h2>
                                    {/* Tooltip in Header - Always visible but highlighted when welcome card dismisses */}
                                    <CustomTooltip title={<WarningContent />} arrow placement="bottom">
                                        <div className={`cursor-help transition-all duration-500 p-1 rounded-full ${!showWelcome ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                                            <HiOutlineInformationCircle className={`w-5 h-5 ${!showWelcome ? 'animate-pulse' : ''}`} />
                                        </div>
                                    </CustomTooltip>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                    {integrationLabel} • Control which projects sync to your workspace
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    <div className="px-6 py-4 space-y-4">
                        {/* Welcome Card with Genie animation */}
                        <WelcomeCard
                            isVisible={showWelcome}
                            onClose={() => setShowWelcome(false)}
                        />

                        {isLoading ? (
                            <>
                                <StatsBarSkeleton />
                                <SearchBarSkeleton />
                                <div className="space-y-3">
                                    {[...Array(5)].map((_, i) => (
                                        <ProjectItemSkeleton key={i} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Stats/Filter Bar */}
                                {localProjects.length > 0 && (
                                    <StatsBar
                                        stats={stats}
                                        activeFilter={viewFilter}
                                        onFilterChange={setViewFilter}
                                    />
                                )}

                                {/* Search Input */}
                                {localProjects.length > 0 && (
                                    <div className="relative">
                                        <HiOutlineSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search projects..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all"
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => setSearchQuery("")}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Projects List */}
                                {filteredProjects.length === 0 ? (
                                    <EmptyState
                                        searchQuery={searchQuery}
                                        viewFilter={viewFilter}
                                        onClearFilters={handleClearFilters}
                                    />
                                ) : (
                                    <div className="space-y-3">
                                        {filteredProjects.map((project) => {
                                            const isChanged = simpleOriginalProjects[project.name] !== undefined &&
                                                simpleOriginalProjects[project.name] !== project.hidden;
                                            return (
                                                <ProjectCard
                                                    key={project.name}
                                                    project={project}
                                                    isChanged={isChanged}
                                                    onToggle={() => handleToggle(project.name)}
                                                    onResync={onResync ? () => handleResync(project.name) : undefined}
                                                    syncingProject={syncingProject}
                                                    integrationLabel={integrationLabel}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 left-0 right-0 shrink-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10">
                    <CustomButton
                        onClick={onClose}
                        variant="cancel"
                        disabled={isSaving}
                    >
                        Close
                    </CustomButton>

                    <div className="flex items-center gap-3">
                        <UnsavedChangesIndicator hasUnsavedChanges={hasChanges} />
                        <CustomButton
                            onClick={handleSave}
                            variant="primary"
                            className={`transition-all duration-200 ${hasChanges
                                ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                                : ''
                                }`}
                            isLoading={isSaving}
                            disabled={isSaving || !hasChanges}
                        >
                            {isSaving ? 'Saving...' : `Save ${hasChanges ? `(${changedProjects.length})` : ''}`}
                        </CustomButton>
                    </div>
                </div>
            </div>

            {/* Resync Confirmation Modal */}
            {showResyncConfirmation && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50" onClick={cancelResync} />
                    <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <HiOutlineRefresh className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Resync Project?
                                </h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                    Are you sure you want to resync <span className="font-medium text-gray-900 dark:text-white">{pendingResyncProject}</span>?
                                </p>
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    This will resync all Epics that you imported for this Project into Cadence. This may take some time depending on the number of epics.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={cancelResync}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmResync}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <HiOutlineRefresh className="w-4 h-4" />
                                Resync Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Drawer>
    );
};

export default ManageProjectsModal;
